/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Timestamp } from "./google/protobuf/timestamp";
import { DoubleValue } from "./google/protobuf/wrappers";
import { WeatherRiskConfigId } from "./id";

export const protobufPackage = "risk";

export enum RiskCategory {
  RISK_CATEGORY_UNSPECIFIED = 0,
  RISK_CATEGORY_NONE = 1,
  RISK_CATEGORY_LOW = 2,
  RISK_CATEGORY_LOW_MEDIUM = 3,
  RISK_CATEGORY_MEDIUM = 4,
  RISK_CATEGORY_MEDIUM_HIGH = 5,
  RISK_CATEGORY_HIGH = 6,
  RISK_CATEGORY_GOLDILOCKS = 7,
  UNRECOGNIZED = -1,
}

export function riskCategoryFromJSON(object: any): RiskCategory {
  switch (object) {
    case 0:
    case "RISK_CATEGORY_UNSPECIFIED":
      return RiskCategory.RISK_CATEGORY_UNSPECIFIED;
    case 1:
    case "RISK_CATEGORY_NONE":
      return RiskCategory.RISK_CATEGORY_NONE;
    case 2:
    case "RISK_CATEGORY_LOW":
      return RiskCategory.RISK_CATEGORY_LOW;
    case 3:
    case "RISK_CATEGORY_LOW_MEDIUM":
      return RiskCategory.RISK_CATEGORY_LOW_MEDIUM;
    case 4:
    case "RISK_CATEGORY_MEDIUM":
      return RiskCategory.RISK_CATEGORY_MEDIUM;
    case 5:
    case "RISK_CATEGORY_MEDIUM_HIGH":
      return RiskCategory.RISK_CATEGORY_MEDIUM_HIGH;
    case 6:
    case "RISK_CATEGORY_HIGH":
      return RiskCategory.RISK_CATEGORY_HIGH;
    case 7:
    case "RISK_CATEGORY_GOLDILOCKS":
      return RiskCategory.RISK_CATEGORY_GOLDILOCKS;
    case -1:
    case "UNRECOGNIZED":
    default:
      return RiskCategory.UNRECOGNIZED;
  }
}

export function riskCategoryToJSON(object: RiskCategory): string {
  switch (object) {
    case RiskCategory.RISK_CATEGORY_UNSPECIFIED:
      return "RISK_CATEGORY_UNSPECIFIED";
    case RiskCategory.RISK_CATEGORY_NONE:
      return "RISK_CATEGORY_NONE";
    case RiskCategory.RISK_CATEGORY_LOW:
      return "RISK_CATEGORY_LOW";
    case RiskCategory.RISK_CATEGORY_LOW_MEDIUM:
      return "RISK_CATEGORY_LOW_MEDIUM";
    case RiskCategory.RISK_CATEGORY_MEDIUM:
      return "RISK_CATEGORY_MEDIUM";
    case RiskCategory.RISK_CATEGORY_MEDIUM_HIGH:
      return "RISK_CATEGORY_MEDIUM_HIGH";
    case RiskCategory.RISK_CATEGORY_HIGH:
      return "RISK_CATEGORY_HIGH";
    case RiskCategory.RISK_CATEGORY_GOLDILOCKS:
      return "RISK_CATEGORY_GOLDILOCKS";
    case RiskCategory.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Settings that describe the risks associated with weather data.
 * <br>
 * Allows saving off the configuration settings used to generate results
 */
export interface WeatherRiskConfig {
  Id: WeatherRiskConfigId | undefined;
  generatedDate: Date | undefined;
  conusConfig:
    | ConusConfig
    | undefined;
  /** The maximum duration of time out that the risk surface should be computed */
  maxRiskForecastMs: number;
  /** Units: Miles-Per-Hour */
  windSpeedRiskConfigMPH:
    | SingleValueRiskConfig
    | undefined;
  /** Units: Miles-Per-Hour */
  windGustRiskConfigMPH:
    | SingleValueRiskConfig
    | undefined;
  /** Units: Fahrenheit */
  apparentTemperatureRiskConfigF:
    | RangeValueRiskConfig
    | undefined;
  /** Units: Miles */
  visibilityRiskConfigMILES: SingleValueRiskConfig | undefined;
  snowAccumulationRiskConfig: SingleValueRiskConfig | undefined;
  iceAccumulationRiskConfig: SingleValueRiskConfig | undefined;
  rainAccumulationRiskConfig: SingleValueRiskConfig | undefined;
  goldilocksConfig: GoldilocksConfig | undefined;
}

export interface ConusConfig {
  /** The size of the cells to use constructing the grid */
  cellSizeDeg: number;
}

export interface GoldilocksConfig {
  /** Units: Miles-Per-Hour */
  windGustConfigMPH:
    | number
    | undefined;
  /** Units: Fahrenheit */
  apparentTemperatureConfigF:
    | RiskValueRange
    | undefined;
  /** Units: Miles */
  visibilityConfigMILES:
    | number
    | undefined;
  /** Units: Fahrenheit */
  dewPointConfigF:
    | number
    | undefined;
  /** Units: Percent */
  skyCoverConfigPERCENT:
    | number
    | undefined;
  /** Units: Millimeters H20 */
  rainAccumulationConfigMMH20: number | undefined;
}

export interface SingleValueRiskConfig {
  /**
   * Value that represents High risk
   * <br>
   * No high risk is used if not present
   */
  highRiskValue:
    | number
    | undefined;
  /**
   * Value that represents Medium-High risk
   * <br>
   * No medium-high risk is used if not present
   */
  mediumHighRiskValue:
    | number
    | undefined;
  /**
   * Value that represents Medium risk
   * <br>
   * No medium risk is used if not present
   */
  mediumRiskValue:
    | number
    | undefined;
  /**
   * Value that represents Low-Medium risk
   * <br>
   * No low-medium risk is used if not present
   */
  lowMediumRiskValue:
    | number
    | undefined;
  /**
   * Value that represents Low risk
   * <br>
   * No low risk is used if not present
   */
  lowRiskValue: number | undefined;
}

export interface RangeValueRiskConfig {
  /**
   * Value that represents High risk
   * <br>
   * No high risk is used if not present
   */
  highRiskValue:
    | RiskValueRange
    | undefined;
  /**
   * Value that represents Medium-High risk
   * <br>
   * No medium-high risk is used if not present
   */
  mediumHighRiskValue:
    | RiskValueRange
    | undefined;
  /**
   * Value that represents Medium risk
   * <br>
   * No medium risk is used if not present
   */
  mediumRiskValue:
    | RiskValueRange
    | undefined;
  /**
   * Value that represents Low-Medium risk
   * <br>
   * No low-medium risk is used if not present
   */
  lowMediumRiskValue:
    | RiskValueRange
    | undefined;
  /**
   * Value that represents Low risk
   * <br>
   * No low risk is used if not present
   */
  lowRiskValue: RiskValueRange | undefined;
}

export interface RiskValueRange {
  lowerBound: number;
  upperBound: number;
}

function createBaseWeatherRiskConfig(): WeatherRiskConfig {
  return {
    Id: undefined,
    generatedDate: undefined,
    conusConfig: undefined,
    maxRiskForecastMs: 0,
    windSpeedRiskConfigMPH: undefined,
    windGustRiskConfigMPH: undefined,
    apparentTemperatureRiskConfigF: undefined,
    visibilityRiskConfigMILES: undefined,
    snowAccumulationRiskConfig: undefined,
    iceAccumulationRiskConfig: undefined,
    rainAccumulationRiskConfig: undefined,
    goldilocksConfig: undefined,
  };
}

export const WeatherRiskConfig = {
  encode(message: WeatherRiskConfig, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.Id !== undefined) {
      WeatherRiskConfigId.encode(message.Id, writer.uint32(10).fork()).ldelim();
    }
    if (message.generatedDate !== undefined) {
      Timestamp.encode(toTimestamp(message.generatedDate), writer.uint32(18).fork()).ldelim();
    }
    if (message.conusConfig !== undefined) {
      ConusConfig.encode(message.conusConfig, writer.uint32(26).fork()).ldelim();
    }
    if (message.maxRiskForecastMs !== 0) {
      writer.uint32(32).int64(message.maxRiskForecastMs);
    }
    if (message.windSpeedRiskConfigMPH !== undefined) {
      SingleValueRiskConfig.encode(message.windSpeedRiskConfigMPH, writer.uint32(42).fork()).ldelim();
    }
    if (message.windGustRiskConfigMPH !== undefined) {
      SingleValueRiskConfig.encode(message.windGustRiskConfigMPH, writer.uint32(50).fork()).ldelim();
    }
    if (message.apparentTemperatureRiskConfigF !== undefined) {
      RangeValueRiskConfig.encode(message.apparentTemperatureRiskConfigF, writer.uint32(58).fork()).ldelim();
    }
    if (message.visibilityRiskConfigMILES !== undefined) {
      SingleValueRiskConfig.encode(message.visibilityRiskConfigMILES, writer.uint32(66).fork()).ldelim();
    }
    if (message.snowAccumulationRiskConfig !== undefined) {
      SingleValueRiskConfig.encode(message.snowAccumulationRiskConfig, writer.uint32(82).fork()).ldelim();
    }
    if (message.iceAccumulationRiskConfig !== undefined) {
      SingleValueRiskConfig.encode(message.iceAccumulationRiskConfig, writer.uint32(90).fork()).ldelim();
    }
    if (message.rainAccumulationRiskConfig !== undefined) {
      SingleValueRiskConfig.encode(message.rainAccumulationRiskConfig, writer.uint32(98).fork()).ldelim();
    }
    if (message.goldilocksConfig !== undefined) {
      GoldilocksConfig.encode(message.goldilocksConfig, writer.uint32(162).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WeatherRiskConfig {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWeatherRiskConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.Id = WeatherRiskConfigId.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.generatedDate = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.conusConfig = ConusConfig.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.maxRiskForecastMs = longToNumber(reader.int64() as Long);
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.windSpeedRiskConfigMPH = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.windGustRiskConfigMPH = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.apparentTemperatureRiskConfigF = RangeValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.visibilityRiskConfigMILES = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.snowAccumulationRiskConfig = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.iceAccumulationRiskConfig = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.rainAccumulationRiskConfig = SingleValueRiskConfig.decode(reader, reader.uint32());
          continue;
        case 20:
          if (tag !== 162) {
            break;
          }

          message.goldilocksConfig = GoldilocksConfig.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WeatherRiskConfig {
    return {
      Id: isSet(object.Id) ? WeatherRiskConfigId.fromJSON(object.Id) : undefined,
      generatedDate: isSet(object.generatedDate) ? fromJsonTimestamp(object.generatedDate) : undefined,
      conusConfig: isSet(object.conusConfig) ? ConusConfig.fromJSON(object.conusConfig) : undefined,
      maxRiskForecastMs: isSet(object.maxRiskForecastMs) ? globalThis.Number(object.maxRiskForecastMs) : 0,
      windSpeedRiskConfigMPH: isSet(object.windSpeedRiskConfigMPH)
        ? SingleValueRiskConfig.fromJSON(object.windSpeedRiskConfigMPH)
        : undefined,
      windGustRiskConfigMPH: isSet(object.windGustRiskConfigMPH)
        ? SingleValueRiskConfig.fromJSON(object.windGustRiskConfigMPH)
        : undefined,
      apparentTemperatureRiskConfigF: isSet(object.apparentTemperatureRiskConfigF)
        ? RangeValueRiskConfig.fromJSON(object.apparentTemperatureRiskConfigF)
        : undefined,
      visibilityRiskConfigMILES: isSet(object.visibilityRiskConfigMILES)
        ? SingleValueRiskConfig.fromJSON(object.visibilityRiskConfigMILES)
        : undefined,
      snowAccumulationRiskConfig: isSet(object.snowAccumulationRiskConfig)
        ? SingleValueRiskConfig.fromJSON(object.snowAccumulationRiskConfig)
        : undefined,
      iceAccumulationRiskConfig: isSet(object.iceAccumulationRiskConfig)
        ? SingleValueRiskConfig.fromJSON(object.iceAccumulationRiskConfig)
        : undefined,
      rainAccumulationRiskConfig: isSet(object.rainAccumulationRiskConfig)
        ? SingleValueRiskConfig.fromJSON(object.rainAccumulationRiskConfig)
        : undefined,
      goldilocksConfig: isSet(object.goldilocksConfig) ? GoldilocksConfig.fromJSON(object.goldilocksConfig) : undefined,
    };
  },

  toJSON(message: WeatherRiskConfig): unknown {
    const obj: any = {};
    if (message.Id !== undefined) {
      obj.Id = WeatherRiskConfigId.toJSON(message.Id);
    }
    if (message.generatedDate !== undefined) {
      obj.generatedDate = message.generatedDate.toISOString();
    }
    if (message.conusConfig !== undefined) {
      obj.conusConfig = ConusConfig.toJSON(message.conusConfig);
    }
    if (message.maxRiskForecastMs !== 0) {
      obj.maxRiskForecastMs = Math.round(message.maxRiskForecastMs);
    }
    if (message.windSpeedRiskConfigMPH !== undefined) {
      obj.windSpeedRiskConfigMPH = SingleValueRiskConfig.toJSON(message.windSpeedRiskConfigMPH);
    }
    if (message.windGustRiskConfigMPH !== undefined) {
      obj.windGustRiskConfigMPH = SingleValueRiskConfig.toJSON(message.windGustRiskConfigMPH);
    }
    if (message.apparentTemperatureRiskConfigF !== undefined) {
      obj.apparentTemperatureRiskConfigF = RangeValueRiskConfig.toJSON(message.apparentTemperatureRiskConfigF);
    }
    if (message.visibilityRiskConfigMILES !== undefined) {
      obj.visibilityRiskConfigMILES = SingleValueRiskConfig.toJSON(message.visibilityRiskConfigMILES);
    }
    if (message.snowAccumulationRiskConfig !== undefined) {
      obj.snowAccumulationRiskConfig = SingleValueRiskConfig.toJSON(message.snowAccumulationRiskConfig);
    }
    if (message.iceAccumulationRiskConfig !== undefined) {
      obj.iceAccumulationRiskConfig = SingleValueRiskConfig.toJSON(message.iceAccumulationRiskConfig);
    }
    if (message.rainAccumulationRiskConfig !== undefined) {
      obj.rainAccumulationRiskConfig = SingleValueRiskConfig.toJSON(message.rainAccumulationRiskConfig);
    }
    if (message.goldilocksConfig !== undefined) {
      obj.goldilocksConfig = GoldilocksConfig.toJSON(message.goldilocksConfig);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WeatherRiskConfig>, I>>(base?: I): WeatherRiskConfig {
    return WeatherRiskConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WeatherRiskConfig>, I>>(object: I): WeatherRiskConfig {
    const message = createBaseWeatherRiskConfig();
    message.Id = (object.Id !== undefined && object.Id !== null)
      ? WeatherRiskConfigId.fromPartial(object.Id)
      : undefined;
    message.generatedDate = object.generatedDate ?? undefined;
    message.conusConfig = (object.conusConfig !== undefined && object.conusConfig !== null)
      ? ConusConfig.fromPartial(object.conusConfig)
      : undefined;
    message.maxRiskForecastMs = object.maxRiskForecastMs ?? 0;
    message.windSpeedRiskConfigMPH =
      (object.windSpeedRiskConfigMPH !== undefined && object.windSpeedRiskConfigMPH !== null)
        ? SingleValueRiskConfig.fromPartial(object.windSpeedRiskConfigMPH)
        : undefined;
    message.windGustRiskConfigMPH =
      (object.windGustRiskConfigMPH !== undefined && object.windGustRiskConfigMPH !== null)
        ? SingleValueRiskConfig.fromPartial(object.windGustRiskConfigMPH)
        : undefined;
    message.apparentTemperatureRiskConfigF =
      (object.apparentTemperatureRiskConfigF !== undefined && object.apparentTemperatureRiskConfigF !== null)
        ? RangeValueRiskConfig.fromPartial(object.apparentTemperatureRiskConfigF)
        : undefined;
    message.visibilityRiskConfigMILES =
      (object.visibilityRiskConfigMILES !== undefined && object.visibilityRiskConfigMILES !== null)
        ? SingleValueRiskConfig.fromPartial(object.visibilityRiskConfigMILES)
        : undefined;
    message.snowAccumulationRiskConfig =
      (object.snowAccumulationRiskConfig !== undefined && object.snowAccumulationRiskConfig !== null)
        ? SingleValueRiskConfig.fromPartial(object.snowAccumulationRiskConfig)
        : undefined;
    message.iceAccumulationRiskConfig =
      (object.iceAccumulationRiskConfig !== undefined && object.iceAccumulationRiskConfig !== null)
        ? SingleValueRiskConfig.fromPartial(object.iceAccumulationRiskConfig)
        : undefined;
    message.rainAccumulationRiskConfig =
      (object.rainAccumulationRiskConfig !== undefined && object.rainAccumulationRiskConfig !== null)
        ? SingleValueRiskConfig.fromPartial(object.rainAccumulationRiskConfig)
        : undefined;
    message.goldilocksConfig = (object.goldilocksConfig !== undefined && object.goldilocksConfig !== null)
      ? GoldilocksConfig.fromPartial(object.goldilocksConfig)
      : undefined;
    return message;
  },
};

function createBaseConusConfig(): ConusConfig {
  return { cellSizeDeg: 0 };
}

export const ConusConfig = {
  encode(message: ConusConfig, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.cellSizeDeg !== 0) {
      writer.uint32(9).double(message.cellSizeDeg);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ConusConfig {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseConusConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 9) {
            break;
          }

          message.cellSizeDeg = reader.double();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ConusConfig {
    return { cellSizeDeg: isSet(object.cellSizeDeg) ? globalThis.Number(object.cellSizeDeg) : 0 };
  },

  toJSON(message: ConusConfig): unknown {
    const obj: any = {};
    if (message.cellSizeDeg !== 0) {
      obj.cellSizeDeg = message.cellSizeDeg;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ConusConfig>, I>>(base?: I): ConusConfig {
    return ConusConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ConusConfig>, I>>(object: I): ConusConfig {
    const message = createBaseConusConfig();
    message.cellSizeDeg = object.cellSizeDeg ?? 0;
    return message;
  },
};

function createBaseGoldilocksConfig(): GoldilocksConfig {
  return {
    windGustConfigMPH: undefined,
    apparentTemperatureConfigF: undefined,
    visibilityConfigMILES: undefined,
    dewPointConfigF: undefined,
    skyCoverConfigPERCENT: undefined,
    rainAccumulationConfigMMH20: undefined,
  };
}

export const GoldilocksConfig = {
  encode(message: GoldilocksConfig, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.windGustConfigMPH !== undefined) {
      DoubleValue.encode({ value: message.windGustConfigMPH! }, writer.uint32(10).fork()).ldelim();
    }
    if (message.apparentTemperatureConfigF !== undefined) {
      RiskValueRange.encode(message.apparentTemperatureConfigF, writer.uint32(18).fork()).ldelim();
    }
    if (message.visibilityConfigMILES !== undefined) {
      DoubleValue.encode({ value: message.visibilityConfigMILES! }, writer.uint32(26).fork()).ldelim();
    }
    if (message.dewPointConfigF !== undefined) {
      DoubleValue.encode({ value: message.dewPointConfigF! }, writer.uint32(34).fork()).ldelim();
    }
    if (message.skyCoverConfigPERCENT !== undefined) {
      DoubleValue.encode({ value: message.skyCoverConfigPERCENT! }, writer.uint32(42).fork()).ldelim();
    }
    if (message.rainAccumulationConfigMMH20 !== undefined) {
      DoubleValue.encode({ value: message.rainAccumulationConfigMMH20! }, writer.uint32(50).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GoldilocksConfig {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGoldilocksConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.windGustConfigMPH = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.apparentTemperatureConfigF = RiskValueRange.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.visibilityConfigMILES = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.dewPointConfigF = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.skyCoverConfigPERCENT = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.rainAccumulationConfigMMH20 = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GoldilocksConfig {
    return {
      windGustConfigMPH: isSet(object.windGustConfigMPH) ? Number(object.windGustConfigMPH) : undefined,
      apparentTemperatureConfigF: isSet(object.apparentTemperatureConfigF)
        ? RiskValueRange.fromJSON(object.apparentTemperatureConfigF)
        : undefined,
      visibilityConfigMILES: isSet(object.visibilityConfigMILES) ? Number(object.visibilityConfigMILES) : undefined,
      dewPointConfigF: isSet(object.dewPointConfigF) ? Number(object.dewPointConfigF) : undefined,
      skyCoverConfigPERCENT: isSet(object.skyCoverConfigPERCENT) ? Number(object.skyCoverConfigPERCENT) : undefined,
      rainAccumulationConfigMMH20: isSet(object.rainAccumulationConfigMMH20)
        ? Number(object.rainAccumulationConfigMMH20)
        : undefined,
    };
  },

  toJSON(message: GoldilocksConfig): unknown {
    const obj: any = {};
    if (message.windGustConfigMPH !== undefined) {
      obj.windGustConfigMPH = message.windGustConfigMPH;
    }
    if (message.apparentTemperatureConfigF !== undefined) {
      obj.apparentTemperatureConfigF = RiskValueRange.toJSON(message.apparentTemperatureConfigF);
    }
    if (message.visibilityConfigMILES !== undefined) {
      obj.visibilityConfigMILES = message.visibilityConfigMILES;
    }
    if (message.dewPointConfigF !== undefined) {
      obj.dewPointConfigF = message.dewPointConfigF;
    }
    if (message.skyCoverConfigPERCENT !== undefined) {
      obj.skyCoverConfigPERCENT = message.skyCoverConfigPERCENT;
    }
    if (message.rainAccumulationConfigMMH20 !== undefined) {
      obj.rainAccumulationConfigMMH20 = message.rainAccumulationConfigMMH20;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GoldilocksConfig>, I>>(base?: I): GoldilocksConfig {
    return GoldilocksConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GoldilocksConfig>, I>>(object: I): GoldilocksConfig {
    const message = createBaseGoldilocksConfig();
    message.windGustConfigMPH = object.windGustConfigMPH ?? undefined;
    message.apparentTemperatureConfigF =
      (object.apparentTemperatureConfigF !== undefined && object.apparentTemperatureConfigF !== null)
        ? RiskValueRange.fromPartial(object.apparentTemperatureConfigF)
        : undefined;
    message.visibilityConfigMILES = object.visibilityConfigMILES ?? undefined;
    message.dewPointConfigF = object.dewPointConfigF ?? undefined;
    message.skyCoverConfigPERCENT = object.skyCoverConfigPERCENT ?? undefined;
    message.rainAccumulationConfigMMH20 = object.rainAccumulationConfigMMH20 ?? undefined;
    return message;
  },
};

function createBaseSingleValueRiskConfig(): SingleValueRiskConfig {
  return {
    highRiskValue: undefined,
    mediumHighRiskValue: undefined,
    mediumRiskValue: undefined,
    lowMediumRiskValue: undefined,
    lowRiskValue: undefined,
  };
}

export const SingleValueRiskConfig = {
  encode(message: SingleValueRiskConfig, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.highRiskValue !== undefined) {
      DoubleValue.encode({ value: message.highRiskValue! }, writer.uint32(10).fork()).ldelim();
    }
    if (message.mediumHighRiskValue !== undefined) {
      DoubleValue.encode({ value: message.mediumHighRiskValue! }, writer.uint32(18).fork()).ldelim();
    }
    if (message.mediumRiskValue !== undefined) {
      DoubleValue.encode({ value: message.mediumRiskValue! }, writer.uint32(26).fork()).ldelim();
    }
    if (message.lowMediumRiskValue !== undefined) {
      DoubleValue.encode({ value: message.lowMediumRiskValue! }, writer.uint32(34).fork()).ldelim();
    }
    if (message.lowRiskValue !== undefined) {
      DoubleValue.encode({ value: message.lowRiskValue! }, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SingleValueRiskConfig {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSingleValueRiskConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.highRiskValue = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.mediumHighRiskValue = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.mediumRiskValue = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.lowMediumRiskValue = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.lowRiskValue = DoubleValue.decode(reader, reader.uint32()).value;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SingleValueRiskConfig {
    return {
      highRiskValue: isSet(object.highRiskValue) ? Number(object.highRiskValue) : undefined,
      mediumHighRiskValue: isSet(object.mediumHighRiskValue) ? Number(object.mediumHighRiskValue) : undefined,
      mediumRiskValue: isSet(object.mediumRiskValue) ? Number(object.mediumRiskValue) : undefined,
      lowMediumRiskValue: isSet(object.lowMediumRiskValue) ? Number(object.lowMediumRiskValue) : undefined,
      lowRiskValue: isSet(object.lowRiskValue) ? Number(object.lowRiskValue) : undefined,
    };
  },

  toJSON(message: SingleValueRiskConfig): unknown {
    const obj: any = {};
    if (message.highRiskValue !== undefined) {
      obj.highRiskValue = message.highRiskValue;
    }
    if (message.mediumHighRiskValue !== undefined) {
      obj.mediumHighRiskValue = message.mediumHighRiskValue;
    }
    if (message.mediumRiskValue !== undefined) {
      obj.mediumRiskValue = message.mediumRiskValue;
    }
    if (message.lowMediumRiskValue !== undefined) {
      obj.lowMediumRiskValue = message.lowMediumRiskValue;
    }
    if (message.lowRiskValue !== undefined) {
      obj.lowRiskValue = message.lowRiskValue;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SingleValueRiskConfig>, I>>(base?: I): SingleValueRiskConfig {
    return SingleValueRiskConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SingleValueRiskConfig>, I>>(object: I): SingleValueRiskConfig {
    const message = createBaseSingleValueRiskConfig();
    message.highRiskValue = object.highRiskValue ?? undefined;
    message.mediumHighRiskValue = object.mediumHighRiskValue ?? undefined;
    message.mediumRiskValue = object.mediumRiskValue ?? undefined;
    message.lowMediumRiskValue = object.lowMediumRiskValue ?? undefined;
    message.lowRiskValue = object.lowRiskValue ?? undefined;
    return message;
  },
};

function createBaseRangeValueRiskConfig(): RangeValueRiskConfig {
  return {
    highRiskValue: undefined,
    mediumHighRiskValue: undefined,
    mediumRiskValue: undefined,
    lowMediumRiskValue: undefined,
    lowRiskValue: undefined,
  };
}

export const RangeValueRiskConfig = {
  encode(message: RangeValueRiskConfig, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.highRiskValue !== undefined) {
      RiskValueRange.encode(message.highRiskValue, writer.uint32(10).fork()).ldelim();
    }
    if (message.mediumHighRiskValue !== undefined) {
      RiskValueRange.encode(message.mediumHighRiskValue, writer.uint32(18).fork()).ldelim();
    }
    if (message.mediumRiskValue !== undefined) {
      RiskValueRange.encode(message.mediumRiskValue, writer.uint32(26).fork()).ldelim();
    }
    if (message.lowMediumRiskValue !== undefined) {
      RiskValueRange.encode(message.lowMediumRiskValue, writer.uint32(34).fork()).ldelim();
    }
    if (message.lowRiskValue !== undefined) {
      RiskValueRange.encode(message.lowRiskValue, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RangeValueRiskConfig {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRangeValueRiskConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.highRiskValue = RiskValueRange.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.mediumHighRiskValue = RiskValueRange.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.mediumRiskValue = RiskValueRange.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.lowMediumRiskValue = RiskValueRange.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.lowRiskValue = RiskValueRange.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RangeValueRiskConfig {
    return {
      highRiskValue: isSet(object.highRiskValue) ? RiskValueRange.fromJSON(object.highRiskValue) : undefined,
      mediumHighRiskValue: isSet(object.mediumHighRiskValue)
        ? RiskValueRange.fromJSON(object.mediumHighRiskValue)
        : undefined,
      mediumRiskValue: isSet(object.mediumRiskValue) ? RiskValueRange.fromJSON(object.mediumRiskValue) : undefined,
      lowMediumRiskValue: isSet(object.lowMediumRiskValue)
        ? RiskValueRange.fromJSON(object.lowMediumRiskValue)
        : undefined,
      lowRiskValue: isSet(object.lowRiskValue) ? RiskValueRange.fromJSON(object.lowRiskValue) : undefined,
    };
  },

  toJSON(message: RangeValueRiskConfig): unknown {
    const obj: any = {};
    if (message.highRiskValue !== undefined) {
      obj.highRiskValue = RiskValueRange.toJSON(message.highRiskValue);
    }
    if (message.mediumHighRiskValue !== undefined) {
      obj.mediumHighRiskValue = RiskValueRange.toJSON(message.mediumHighRiskValue);
    }
    if (message.mediumRiskValue !== undefined) {
      obj.mediumRiskValue = RiskValueRange.toJSON(message.mediumRiskValue);
    }
    if (message.lowMediumRiskValue !== undefined) {
      obj.lowMediumRiskValue = RiskValueRange.toJSON(message.lowMediumRiskValue);
    }
    if (message.lowRiskValue !== undefined) {
      obj.lowRiskValue = RiskValueRange.toJSON(message.lowRiskValue);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RangeValueRiskConfig>, I>>(base?: I): RangeValueRiskConfig {
    return RangeValueRiskConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RangeValueRiskConfig>, I>>(object: I): RangeValueRiskConfig {
    const message = createBaseRangeValueRiskConfig();
    message.highRiskValue = (object.highRiskValue !== undefined && object.highRiskValue !== null)
      ? RiskValueRange.fromPartial(object.highRiskValue)
      : undefined;
    message.mediumHighRiskValue = (object.mediumHighRiskValue !== undefined && object.mediumHighRiskValue !== null)
      ? RiskValueRange.fromPartial(object.mediumHighRiskValue)
      : undefined;
    message.mediumRiskValue = (object.mediumRiskValue !== undefined && object.mediumRiskValue !== null)
      ? RiskValueRange.fromPartial(object.mediumRiskValue)
      : undefined;
    message.lowMediumRiskValue = (object.lowMediumRiskValue !== undefined && object.lowMediumRiskValue !== null)
      ? RiskValueRange.fromPartial(object.lowMediumRiskValue)
      : undefined;
    message.lowRiskValue = (object.lowRiskValue !== undefined && object.lowRiskValue !== null)
      ? RiskValueRange.fromPartial(object.lowRiskValue)
      : undefined;
    return message;
  },
};

function createBaseRiskValueRange(): RiskValueRange {
  return { lowerBound: 0, upperBound: 0 };
}

export const RiskValueRange = {
  encode(message: RiskValueRange, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.lowerBound !== 0) {
      writer.uint32(9).double(message.lowerBound);
    }
    if (message.upperBound !== 0) {
      writer.uint32(17).double(message.upperBound);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RiskValueRange {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRiskValueRange();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 9) {
            break;
          }

          message.lowerBound = reader.double();
          continue;
        case 2:
          if (tag !== 17) {
            break;
          }

          message.upperBound = reader.double();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RiskValueRange {
    return {
      lowerBound: isSet(object.lowerBound) ? globalThis.Number(object.lowerBound) : 0,
      upperBound: isSet(object.upperBound) ? globalThis.Number(object.upperBound) : 0,
    };
  },

  toJSON(message: RiskValueRange): unknown {
    const obj: any = {};
    if (message.lowerBound !== 0) {
      obj.lowerBound = message.lowerBound;
    }
    if (message.upperBound !== 0) {
      obj.upperBound = message.upperBound;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RiskValueRange>, I>>(base?: I): RiskValueRange {
    return RiskValueRange.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RiskValueRange>, I>>(object: I): RiskValueRange {
    const message = createBaseRiskValueRange();
    message.lowerBound = object.lowerBound ?? 0;
    message.upperBound = object.upperBound ?? 0;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = Math.trunc(date.getTime() / 1_000);
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof globalThis.Date) {
    return o;
  } else if (typeof o === "string") {
    return new globalThis.Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
